import {
  useLazyGetFindingsQuery,
  useExportToExcelMutation,
  useProjectId,
  usePackageId,
  useProject,
} from '@consigli/hooks';
import {
  Button,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@consigli/shadcn';
import { MIME_TYPES, saveBlobBase64, saveJson } from '@consigli/utils';
import { DownloadIcon, Loader2Icon } from 'lucide-react';
import { useCallback, useState } from 'react';

import { errorToast, successToast } from '@/components';
import { createFindingsExcelExport } from '@/utils/excel-export';

/**
 * Button with dropdown to export findings as Excel or JSON.
 */
export const ExportFindingsButton = () => {
  const projectId = useProjectId();
  const packageId = usePackageId();
  const { project } = useProject(projectId);
  const projectName = project?.name || 'findings';

  const [getAllFindings, { isFetching }] = useLazyGetFindingsQuery();
  const [exportToExcel, { isLoading: isExportingExcel }] = useExportToExcelMutation();
  const [isExporting, setIsExporting] = useState(false);

  const isLoading = isFetching || isExportingExcel || isExporting;

  const handleExportExcel = useCallback(async () => {
    setIsExporting(true);
    try {
      const findings = await getAllFindings({
        projectId,
        packageId,
        page: 'all',
      }).unwrap();

      const excelExport = createFindingsExcelExport(findings.results, projectName);
      const { base64Data } = await exportToExcel({
        projectId,
        packageId,
        excelExport,
      }).unwrap();

      saveBlobBase64(`${projectName}_findings.xlsx`, base64Data, MIME_TYPES.xlsx);
      successToast({ message: 'Findings exported as Excel successfully' });
    } catch (error) {
      console.error('Error exporting findings as Excel:', error);
      errorToast({ message: 'Failed to export findings as Excel' });
    } finally {
      setIsExporting(false);
    }
  }, [getAllFindings, exportToExcel, projectId, packageId, projectName]);

  const handleExportJson = useCallback(async () => {
    setIsExporting(true);
    try {
      const findings = await getAllFindings({
        projectId,
        packageId,
        page: 'all',
      }).unwrap();

      saveJson(`${projectName}_findings.json`, findings.results);
      successToast({ message: 'Findings exported as JSON successfully' });
    } catch (error) {
      console.error('Error exporting findings as JSON:', error);
      errorToast({ message: 'Failed to export findings as JSON' });
    } finally {
      setIsExporting(false);
    }
  }, [getAllFindings, projectId, packageId, projectName]);

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" disabled={isLoading} className="gap-2">
          {isLoading ? (
            <Loader2Icon className="h-4 w-4 animate-spin" />
          ) : (
            <DownloadIcon className="h-4 w-4" />
          )}
          Export Findings
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onSelect={handleExportExcel} disabled={isLoading}>
          Export as Excel
        </DropdownMenuItem>
        <DropdownMenuItem onSelect={handleExportJson} disabled={isLoading}>
          Export as JSON
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};
